import { useCourseContext } from '@Features/Course/Providers/CourseProvider/CourseProvider'
import { parentBasePath, playerBasePath } from '@Features/Course/Utilities/playerPaths'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { useSearchParams } from 'react-router-dom'
import styled from 'styled-components'

type BlockingUnit = { id: number; name: string; type: string }

type ParsedUnitError =
	| { kind: 'locked'; blockingUnit?: BlockingUnit }
	| { kind: 'not_found' }
	| { kind: 'generic' }

/**
 * Maps a unit query error onto the three states the backend contract defines:
 * 403 SEQUENTIAL_UNIT_LOCKED (with the earliest blocking unit), 404 for
 * deactivated/deleted units, and everything else.
 */
const parseUnitError = (error: unknown): ParsedUnitError => {
	const err = error as { status?: unknown; data?: { data?: { code?: string; blocking_unit?: BlockingUnit } } } | undefined
	if (err && typeof err === 'object') {
		if (err.status === 403 && err.data?.data?.code === 'SEQUENTIAL_UNIT_LOCKED') {
			return { kind: 'locked', blockingUnit: err.data.data.blocking_unit }
		}
		if (err.status === 404) return { kind: 'not_found' }
	}
	return { kind: 'generic' }
}

type Props = {
	error: unknown
	onRetry?: () => void
}

/**
 * Shared error state for every unit viewer (video, document, webcontent, test,
 * webinar, assignment and the generic unit shell).
 *
 * - Sequential lock (403): the interceptor already snackbars the backend's
 *   localized message (show: true); here we additionally redirect — replace —
 *   to the blocking unit so deep links land the student on their next
 *   actionable unit. The lock text stays as the fallback body meanwhile.
 * - 404: the unit is gone — say so, with a way back to the course.
 * - Anything else: generic message + retry.
 */
const UnitErrorState: React.FC<Props> = ({ error, onRetry }) => {
	const [t] = useTranslation('course')
	const navigate = useNavigate()
	const { label, cId } = useParams()
	const [searchParams] = useSearchParams()
	const { parentType } = useCourseContext()

	const parsed = useMemo(() => parseUnitError(error), [error])
	const blockingUnit = parsed.kind === 'locked' ? parsed.blockingUnit : undefined

	useEffect(() => {
		if (!blockingUnit || !label || !cId) return
		const pathname = `${playerBasePath(parentType ?? 'classroom', label, cId)}/${blockingUnit.type}/${blockingUnit.id}`
		navigate({ pathname, search: searchParams.toString() }, { replace: true })
	}, [blockingUnit?.id])

	if (parsed.kind === 'locked') {
		const FSI = String.fromCharCode(0x2068)
		const PDI = String.fromCharCode(0x2069)
		return (
			<StateWrapper>
				<StateText>
					{blockingUnit
						? t('complete_first', { name: FSI + blockingUnit.name + PDI, interpolation: { escapeValue: false } })
						: t('content_unavailable')}
				</StateText>
			</StateWrapper>
		)
	}

	if (parsed.kind === 'not_found') {
		const backToCourse = () => {
			if (label && cId) navigate(`${parentBasePath(parentType ?? 'classroom', label)}/courses/${cId}`)
			else navigate(-1)
		}
		return (
			<StateWrapper>
				<StateText>{t('content_unavailable')}</StateText>
				<StateButton type="button" onClick={backToCourse}>
					{t('back_to_course')}
				</StateButton>
			</StateWrapper>
		)
	}

	return (
		<StateWrapper>
			<StateText>{t('generic_error')}</StateText>
			{onRetry && (
				<StateButton type="button" onClick={onRetry}>
					{t('retry')}
				</StateButton>
			)}
		</StateWrapper>
	)
}

export default UnitErrorState

const StateWrapper = styled.div`
	width: 100%;
	min-height: 200px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 16px;
	padding: 32px var(--screen-pad-x);
	text-align: center;
`

const StateText = styled.p`
	margin: 0;
	font: var(--fw-medium) 15px/1.6 var(--font-sans);
	color: var(--text-secondary);
`

const StateButton = styled.button`
	height: 40px;
	padding: 0 24px;
	border: 1px solid var(--blue);
	border-radius: var(--r-md);
	background: transparent;
	color: var(--blue);
	font: var(--fw-bold) 13px var(--font-sans);
	font-family: var(--font-sans);
	cursor: pointer;
	transition: background var(--dur-fast) var(--ease), color var(--dur-fast) var(--ease);
	&:hover {
		background: var(--blue);
		color: #fff;
	}
`
